import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Handshake,
  Buildings,
  Users,
  Plant,
  GraduationCap,
  Sparkle,
  ClipboardText,
  ShieldCheck,
  Heart,
  CheckCircle,
  ArrowLeft,
  Lightbulb,
  Compass,
  PencilRuler,
  ListChecks,
  ChartLineUp,
} from '@phosphor-icons/react';

const CORE_SERVICES = [
  {
    icon: ClipboardText,
    title: 'دراسة الجاهزية والاحتياج',
    desc: 'قراءة منظمة لخصائص الموقع والكوادر والأسر المستفيدة، وتحديد نطاق العمل المناسب.',
  },
  {
    icon: PencilRuler,
    title: 'تصميم البيئة التعليمية',
    desc: 'تصميم مساحات آمنة ومحفّزة تناسب المرحلة العمرية وطبيعة مقر العمل.',
  },
  {
    icon: Buildings,
    title: 'تخطيط الأركان والبيئات الصفية',
    desc: 'تحديد متطلبات الأثاث والمواد التعليمية ومواءمتها مع المنهج والهوية المؤسسية.',
  },
  {
    icon: ListChecks,
    title: 'النماذج التشغيلية والتنظيمية',
    desc: 'بناء النماذج والإجراءات اليومية التي تضمن انتظام التشغيل وجودة الخدمة.',
  },
  {
    icon: ShieldCheck,
    title: 'سياسات التشغيل والحوكمة',
    desc: 'إعداد السياسات الإدارية والتشغيلية بما يتوافق مع معايير الجودة والسلامة.',
  },
  {
    icon: Heart,
    title: 'تجربة الأسرة والتسجيل',
    desc: 'تصميم رحلة الأسرة من التسجيل إلى المتابعة اليومية بأسلوب احترافي وداعم.',
  },
  {
    icon: GraduationCap,
    title: 'تطوير الكوادر التعليمية والإدارية',
    desc: 'برامج تأهيل وتطوير عملية ترفع كفاءة الكادر وتدعم جودة الممارسات.',
  },
  {
    icon: Sparkle,
    title: 'التأهيل للاعتماد والجودة',
    desc: 'تجهيز المنشأة لمتطلبات الاعتماد عبر معايير ومؤشرات قابلة للقياس.',
  },
  {
    icon: Handshake,
    title: 'مواءمة التشغيل مع بيئة العمل',
    desc: 'دمج خدمة الروضة ضمن سياق مقر العمل بما يدعم انتظام الأسرة وراحتها.',
  },
];

const AUDIENCES = [
  'الجهات الحكومية والشبه حكومية',
  'الشركات والقطاع الخاص',
  'الجهات غير الربحية',
  'المجمعات الإدارية والتشغيلية',
  'المشاريع الناشئة في الطفولة المبكرة المؤسسية',
];

const PHASES = [
  {
    icon: Compass,
    title: 'الاستكشاف وتحديد الاحتياج',
    desc: 'دراسة السياق والاحتياج والفئات المستهدفة والمخرجات المتوقعة.',
  },
  {
    icon: Lightbulb,
    title: 'الدراسة والتصميم',
    desc: 'إعداد التصور المتكامل للبيئة والنموذج التربوي والتشغيلي وفق المعايير المعتمدة.',
  },
  {
    icon: ListChecks,
    title: 'خطة الجاهزية والتأسيس',
    desc: 'إعداد السياسات والنماذج ومتطلبات البيئة والكوادر وخطة الجاهزية للانطلاق.',
  },
  {
    icon: ChartLineUp,
    title: 'دعم الانطلاق وقياس التقدم',
    desc: 'دعم استشاري خلال الانطلاق وقياس التقدم وتقديم توصيات التحسين.',
  },
];

const WorkplaceNurseriesPage = () => (
  <main
    data-testid="workplace-nurseries-page"
    className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
    dir="rtl"
  >
    {/* Hero */}
    <section className="bg-[#1F3D2B] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-white/30 text-[#D8E0C9] text-xs sm:text-sm mb-5">
            قطاع مؤسسي متخصص
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-5">
            استشارات تأسيس وتطوير الحضانات ورياض الأطفال
          </h1>
          <p className="text-base sm:text-lg text-[#D8E0C9] leading-9 max-w-3xl">
            حلول استشارية متكاملة لدعم تأسيس وتطوير الحضانات ورياض الأطفال،
            بدءًا من دراسة الجاهزية والاحتياج، مرورًا بتصميم النموذج التربوي
            والتشغيلي، ووصولًا إلى خطط الجاهزية ودعم الانطلاق وفق أفضل
            الممارسات.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/quotation?service=workplace-nurseries"
              data-testid="hero-quote-btn"
              className="bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-7 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              اطلب عرض سعر
              <ArrowLeft size={16} className="rtl-flip" />
            </Link>
            <Link
              to="/booking"
              data-testid="hero-booking-btn"
              className="border border-white/40 text-white hover:bg-white/10 rounded-xl px-7 py-3.5 text-sm font-medium inline-flex items-center justify-center transition-colors"
            >
              احجز جلسة استشارية
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Overview / About */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start"
        >
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
              نبذة عن الخدمة
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic'] leading-tight">
              مرافقة استشارية متكاملة لخدمة طفولة مبكرة مؤسسية
            </h2>
            <p className="text-[#3D512D] leading-9 text-base">
              نعمل مع الجهات الراغبة في تأسيس أو تطوير روضات داخل مقار عملها على
              بناء نموذج تشغيلي متكامل، يبدأ من دراسة الجاهزية وينتهي بمرافقة
              تشغيلية فعلية. منهجيتنا تركز على جودة البيئة، انتظام التشغيل،
              تجربة الأسرة، وتأهيل الكادر، بأسلوب مؤسسي يحترم خصوصية كل جهة.
            </p>
          </div>
          <div className="bg-[#F5F2EC] border border-[#E6E0D6] rounded-[20px] p-6">
            <h3 className="text-base font-bold text-[#1F3D2B] mb-4 flex items-center gap-2">
              <Users size={20} weight="duotone" className="text-[#4A641F]" />
              الفئات المستهدفة
            </h3>
            <ul className="space-y-2.5">
              {AUDIENCES.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-2 text-sm text-[#3D512D] leading-7"
                >
                  <CheckCircle
                    size={14}
                    weight="fill"
                    className="text-[#4A641F] mt-1 shrink-0"
                  />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Core services grid */}
    <section className="py-16 sm:py-20 bg-[#F5F2EC]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            نطاق العمل
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            ما نقدّمه داخل هذا القطاع
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            باقة خدمات استشارية متكاملة تتكامل لبناء بيئة طفولة مبكرة مؤسسية
            بمعايير عالية.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CORE_SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                data-testid={`workplace-service-card-${i}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white rounded-2xl border border-[#E6E0D6] p-6 hover:border-[#4A641F] transition-colors"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#F5F2EC] flex items-center justify-center mb-4">
                  <Icon size={22} weight="duotone" className="text-[#1F3D2B]" />
                </div>
                <h3 className="text-base font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic'] leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-[#6B7565] leading-7">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Phases */}
    <section className="py-16 sm:py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            مراحل العمل
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            كيف نعمل معكم
          </h2>
          <p className="text-[#6B7565] max-w-2xl mx-auto leading-8 text-sm sm:text-base">
            منهجية واضحة، مراحل متسلسلة، ومخرجات قابلة للقياس في كل مرحلة.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PHASES.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="bg-white rounded-2xl border border-[#E6E0D6] p-6 relative"
              >
                <span className="absolute top-4 left-4 text-xs text-[#92A082] font-bold">
                  0{i + 1}
                </span>
                <div className="w-11 h-11 rounded-xl bg-[#F5F2EC] flex items-center justify-center mb-4">
                  <Icon size={20} weight="duotone" className="text-[#4A641F]" />
                </div>
                <h3 className="text-sm font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic'] leading-tight">
                  {p.title}
                </h3>
                <p className="text-xs text-[#6B7565] leading-7">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#1F3D2B]" data-testid="workplace-cta-section">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 font-['IBM_Plex_Sans_Arabic'] leading-tight">
            مهتمون بتأسيس أو تطوير روضة داخل مقر عملكم؟
          </h2>
          <p className="text-[#D8E0C9] leading-9 text-sm sm:text-base mb-8">
            شاركونا تفاصيل احتياجكم وسنُعد لكم عرض سعر مخصص يتناسب مع طبيعة
            الجهة ونطاق العمل المطلوب.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/quotation?service=workplace-nurseries"
              data-testid="cta-quote-btn"
              className="bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl px-7 py-3.5 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              اطلب عرض سعر مخصص
              <ArrowLeft size={16} className="rtl-flip" />
            </Link>
            <Link
              to="/booking"
              data-testid="cta-booking-btn"
              className="border border-white/40 text-white hover:bg-white/10 rounded-xl px-7 py-3.5 text-sm font-medium inline-flex items-center justify-center transition-colors"
            >
              احجز جلسة استشارية
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  </main>
);

export default WorkplaceNurseriesPage;
